import { Slot } from 'radix-ui'
import type { ElementType, HTMLAttributes, ReactNode } from 'react'

/**
 * THE shimmer: the one travelling band in the app, and the only way to put one on
 * a surface.
 *
 * Every sweep used to be its own CSS — the login card's ring, the edge on badges
 * and cards, the band through the wordmark, the rail mark's tint, the skeleton's
 * pulse — each with its own keyframes, and they drifted (the login card once
 * flashed at twice the speed of every other edge). Now there is one CSS primitive
 * (`[data-shimmer]` in index.css, modelled on the login card) and this module is
 * its only door: it stamps the three attributes the CSS reads, so timing, band
 * width, colours and reduced-motion behaviour come from one place.
 *
 *   variant  WHERE the band paints — `border` (a 1px ring over the element's own
 *            border), `text` (through the glyphs, over an aria-hidden copy),
 *            `icon` (the glyph's colour walks the band's stops), `skeleton` (the
 *            band across a loading placeholder), `lockup` (the mark and the word
 *            as one surface: one band crosses the glyph, then the letters).
 *   trigger  WHEN it travels — `always` (ambient: the login card, a skeleton),
 *            `hover` (a response: while the element or its `shimmerHost` is
 *            hovered or keyboard-focused), `once` (one pass, e.g. on selection).
 *   paint    WHICH ramp — `brand` for identity surfaces (the auth card, badges,
 *            the wordmark), `neutral` for everything else.
 */

export type ShimmerVariant = 'border' | 'text' | 'icon' | 'skeleton' | 'lockup'
export type ShimmerTrigger = 'always' | 'hover' | 'once'
export type ShimmerPaint = 'brand' | 'neutral'

export interface ShimmerOptions {
  variant: ShimmerVariant
  trigger?: ShimmerTrigger
  paint?: ShimmerPaint
}

const DEFAULT_TRIGGER: Record<ShimmerVariant, ShimmerTrigger> = {
  border: 'hover',
  text: 'hover',
  icon: 'hover',
  skeleton: 'always',
  lockup: 'always',
}

// Text and icons are identity (a wordmark, the mark): a neutral band is white on
// ink, which cannot show over near-white text. Edges and placeholders are chrome.
const DEFAULT_PAINT: Record<ShimmerVariant, ShimmerPaint> = {
  border: 'neutral',
  text: 'brand',
  icon: 'brand',
  skeleton: 'neutral',
  lockup: 'brand',
}

/**
 * The attributes that put the shimmer on an element you already render — spread
 * them onto it. For a component that owns its element (a badge, a card, a tile)
 * this keeps the markup unchanged.
 */
export function shimmer({ variant, trigger, paint }: ShimmerOptions) {
  return {
    'data-shimmer': variant,
    'data-shimmer-trigger': trigger ?? DEFAULT_TRIGGER[variant],
    'data-shimmer-paint': paint ?? DEFAULT_PAINT[variant],
  } as const
}

/**
 * Marks an element whose hover or focus lights the `hover` shimmers inside it — a
 * link lighting its wordmark, a button lighting its label.
 */
export const shimmerHost = { 'data-shimmer-host': '' } as const

type ShimmerProps = Partial<ShimmerOptions> &
  HTMLAttributes<HTMLElement> & {
    /** Put the shimmer on the single child instead of rendering a wrapper. Not for `text`. */
    asChild?: boolean
    children?: ReactNode
  }

/**
 * The component form. `variant="text"` renders the band's aria-hidden duplicate
 * for you, so the words are announced once; every other variant renders (or, with
 * `asChild`, becomes) a single element carrying the attributes.
 */
export function Shimmer({
  variant = 'border',
  trigger,
  paint,
  asChild = false,
  children,
  ...props
}: ShimmerProps) {
  const attrs = shimmer({ variant, trigger, paint })

  // A lockup is text plus a mark: the same aria-hidden copy carries the band
  // through the letters, and a second aria-hidden layer — the same box, so the
  // same band position — carries it through the mark, masked to its outline.
  // The caller sets `--shimmer-mark` and `--shimmer-mark-size` (see BrandLockup).
  if (variant === 'lockup') {
    return (
      <span {...attrs} {...props}>
        {children}
        <span data-shimmer-band="" aria-hidden>
          {children}
        </span>
        <span data-shimmer-band="mark" aria-hidden />
      </span>
    )
  }

  if (variant === 'text') {
    return (
      <span {...attrs} {...props}>
        {children}
        <span data-shimmer-band="" aria-hidden>
          {children}
        </span>
      </span>
    )
  }

  const Comp: ElementType = asChild ? Slot.Root : variant === 'skeleton' ? 'div' : 'span'
  return (
    <Comp {...attrs} {...props}>
      {children}
    </Comp>
  )
}
